'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Copy, Check, ArrowRight, CalendarDays, Users, TrendingUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Add helper to call the welcome email edge function
async function sendWelcomeEmail(email: string, fullName: string, role: string) {
  try {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    if (!supabaseUrl) return;
    await fetch(`${supabaseUrl}/functions/v1/send-welcome-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, fullName, role }),
    });
  } catch {
    // Non-blocking — don't fail signup if email fails
  }
}

// Add helper to call the welcome SMS edge function
async function sendWelcomeSms(phone: string, fullName: string, role: string) {
  try {
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    if (!supabaseUrl || !phone) return;
    await fetch(`${supabaseUrl}/functions/v1/send-welcome-sms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, fullName, role }),
    });
  } catch {
    // Non-blocking — don't fail signup if SMS fails
  }
}

type AuthMode = 'login' | 'signup';
type UserRole = 'attendee' | 'organizer' | 'admin';

type LoginFormData = {
  email: string;
  password: string;
  remember: boolean;
};

type SignupFormData = {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
  organizationName?: string;
  agreeTerms: boolean;
};

const demoCredentials = [
  { role: 'attendee' as UserRole, label: 'Attendee', email: 'priya.nair@outlook.com', password: 'Attend2026!' },
  { role: 'organizer' as UserRole, label: 'Organizer', email: 'hello@techforward.io', password: 'Organize2026!' },
  { role: 'admin' as UserRole, label: 'Admin', email: 'admin@eventify.io', password: 'Admin2026#' },
];

const roleDestination = (role: UserRole) => {
  if (role === 'admin') return '/admin-panel';
  if (role === 'organizer') return '/organizer-dashboard';
  return '/attendee-dashboard';
};

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginFormData>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupFormData>({
    defaultValues: { name: '', email: '', phone: '', password: '', confirmPassword: '', role: 'attendee', agreeTerms: false },
  });

  const watchedSignupRole = signupForm.watch('role');

  const handleCopy = (value: string, key: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleAutofill = (cred: (typeof demoCredentials)[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    toast.success(`Autofilled ${cred.label} credentials`);
  };

  const onLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      const role = result?.user?.user_metadata?.role as UserRole || 'attendee';
      toast.success(`Welcome back!`);
      router.push(roleDestination(role));
      router.refresh();
    } catch (err: any) {
      loginForm.setError('email', { message: err?.message || 'Invalid email or password' });
    } finally {
      setIsLoading(false);
    }
  };

  const onSignup = async (data: SignupFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.name,
        role: data.role,
        organizationName: data.role === 'organizer' ? data.organizationName : undefined,
      });
      toast.success('Account created! Signing you in…');
      // Send role-based welcome email (non-blocking)
      sendWelcomeEmail(data.email, data.name, data.role);
      // Send role-based welcome SMS (non-blocking)
      if (data.phone) sendWelcomeSms(data.phone, data.name, data.role);
      // Auto sign-in after signup
      const result = await signIn(data.email, data.password);
      const role = result?.user?.user_metadata?.role as UserRole || data.role;
      router.push(roleDestination(role));
      router.refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Could not create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 gradient-primary flex-col justify-between p-12 text-white">
        <Link href="/" className="flex items-center gap-3">
          <AppLogo size={40} />
          <span className="font-extrabold text-2xl tracking-tight">Eventz</span>
        </Link>
        <div>
          <h1 className="text-4xl font-extrabold leading-tight mb-6">
            Your next great<br />experience is one<br />ticket away.
          </h1>
          <div className="flex flex-col gap-4">
            {[
              { icon: CalendarDays, text: '12 events live across 5 categories' },
              { icon: Users, text: '5,944 tickets sold to attendees like you' },
              { icon: TrendingUp, text: 'Organizers earned $728K on the platform' },
            ].map((item) => {
              const ItemIcon = item.icon;
              return (
                <div key={`brand-${item.text.slice(0, 20)}`} className="flex items-center gap-3 text-white/80">
                  <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <ItemIcon size={17} />
                  </div>
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
        <p className="text-white/50 text-xs">© 2026 Eventz, Inc.</p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link href="/" className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-extrabold text-xl text-foreground">Eventz</span>
          </Link>

          {/* Mode toggle */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            {(['login', 'signup'] as AuthMode[]).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  mode === m ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(onLogin)} className="flex flex-col gap-5 fade-in">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email address</label>
                <input
                  type="email"
                  {...loginForm.register('email', { required: 'Email is required' })}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  placeholder="you@example.com"
                />
                {loginForm.formState.errors.email && (
                  <p className="mt-1.5 text-xs text-danger">{loginForm.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <div className="flex justify-between mb-1.5">
                  <label className="text-sm font-semibold text-foreground">Password</label>
                  <Link href="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</Link>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Password is required' })}
                    className="w-full px-4 py-3 pr-11 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1.5 text-xs text-danger">{loginForm.formState.errors.password.message}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  {...loginForm.register('remember')}
                  className="w-4 h-4 rounded border-border accent-primary"
                />
                <label htmlFor="remember" className="text-sm text-muted-foreground">Remember me for 30 days</label>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isLoading ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Sign In <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}

          {/* Signup Form */}
          {mode === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(onSignup)} className="flex flex-col gap-4 fade-in">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Full name</label>
                <input
                  type="text"
                  {...signupForm.register('name', { required: 'Full name is required' })}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  placeholder="Priya Nair"
                />
                {signupForm.formState.errors.name && (
                  <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Phone number <span className="text-muted-foreground font-normal">(optional — for welcome SMS)</span></label>
                <input
                  type="tel"
                  {...signupForm.register('phone')}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  placeholder="+1 555 000 0000"
                />
              </div>

              {/* Role selection for signup */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">I am joining as</label>
                <div className="flex gap-2">
                  {(['attendee', 'organizer'] as UserRole[]).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => signupForm.setValue('role', r)}
                      className={`flex-1 py-2.5 text-sm font-semibold rounded-xl border transition-all duration-150 capitalize ${
                        watchedSignupRole === r
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-card text-muted-foreground border-border hover:border-primary/50'
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              {watchedSignupRole === 'organizer' && (
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Organization name</label>
                  <input
                    type="text"
                    {...signupForm.register('organizationName', {
                      required: watchedSignupRole === 'organizer' ? 'Organization name is required' : false,
                    })}
                    className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                    placeholder="TechForward Events"
                  />
                  {signupForm.formState.errors.organizationName && (
                    <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.organizationName.message}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email address</label>
                <input
                  type="email"
                  {...signupForm.register('email', { required: 'Email is required' })}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                  placeholder="you@example.com"
                />
                {signupForm.formState.errors.email && (
                  <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.email.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })}
                    className="w-full px-4 py-3 pr-11 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                    placeholder="Min. 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Toggle password"
                  >
                    {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.password.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                    className="w-full px-4 py-3 pr-11 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Toggle confirm password"
                  >
                    {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="mt-1.5 text-xs text-danger">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  {...signupForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                  className="w-4 h-4 mt-0.5 rounded border-border accent-primary"
                />
                <label htmlFor="agreeTerms" className="text-sm text-muted-foreground">
                  I agree to the{' '}
                  <span className="text-primary hover:underline cursor-pointer">Terms of Service</span>{' '}
                  and{' '}
                  <span className="text-primary hover:underline cursor-pointer">Privacy Policy</span>
                </label>
              </div>
              {signupForm.formState.errors.agreeTerms && (
                <p className="text-xs text-danger">{signupForm.formState.errors.agreeTerms.message}</p>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isLoading ? (
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Create Account <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}

          {/* Demo credentials */}
          <div className="mt-8 p-4 bg-muted rounded-2xl border border-border">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Demo Accounts — click to autofill</p>
            <div className="flex flex-col gap-2">
              {demoCredentials.map((cred) => (
                <div key={`cred-${cred.role}`} className="flex items-center gap-3 p-2.5 bg-card rounded-xl border border-border hover:border-primary/50 transition-all">
                  <span className="text-xs font-semibold text-muted-foreground w-20 shrink-0">{cred.label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground truncate font-medium">{cred.email}</p>
                    <p className="text-xs text-muted-foreground">{cred.password}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleCopy(cred.email, `email-${cred.role}`)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
                      title="Copy email"
                    >
                      {copiedField === `email-${cred.role}` ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAutofill(cred)}
                      className="px-2.5 py-1 text-xs font-semibold text-primary bg-secondary rounded-lg hover:opacity-80 transition-all"
                    >
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}